import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Trophy } from "lucide-react";
import { formatPrice, type TopEarner } from "@nh/shared";

/**
 * ═══════════════════════════════════════════════════════════════════
 * La card OSCURA del dashboard (referencia "Sell Order"/"Reports" de
 * Anahí, 27-ago): el TOP de socios que más comisiones han generado.
 * Cada fila abre su expediente de pago — de ver el número a pagarlo,
 * un clic.
 * ═══════════════════════════════════════════════════════════════════
 */
export function TopEarnersCard({ items }: { items: TopEarner[] }) {
  const { t } = useTranslation();

  return (
    <section className="overflow-hidden rounded-[28px] bg-ds-ink p-5 text-white shadow-ds-float">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-[15px] font-semibold">
          <Trophy className="h-4 w-4 text-amber-400" />
          {t("Top de socios")}
        </h2>
        <Link
          to="/admin/commissions"
          aria-label={t("Ver el libro de comisiones")}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 transition hover:bg-white/20"
        >
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <p className="mt-0.5 text-[12px] text-white/50">
        {t("Quienes más comisiones han generado. Clic abre su expediente.")}
      </p>

      {items.length === 0 ? (
        <p className="mt-5 rounded-2xl bg-white/5 px-4 py-3 text-[13px] text-white/60">
          {t("Cuando el motor escriba comisiones, el top aparece aquí.")}
        </p>
      ) : (
        <ol className="mt-4 grid grid-cols-1 gap-1">
          {items.map((earner, index) => (
            <li key={earner.userId} className="min-w-0">
              <Link
                to={`/admin/commissions/u/${earner.userId}`}
                className="flex items-center gap-2.5 rounded-2xl px-2.5 py-2.5 transition hover:bg-white/10"
              >
                <span className="w-5 shrink-0 text-center font-mono text-[13px] font-bold text-white/40">
                  {index + 1}
                </span>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ds-violet-pop text-[11px] font-bold">
                  {earner.name
                    .split(/\s+/)
                    .slice(0, 2)
                    .map((word) => word[0])
                    .join("")
                    .toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-medium">{earner.name}</span>
                  {/* `truncate` también aquí: "1 comisión · debe
                      $1,800.00" ensanchaba la fila y empujaba el monto
                      fuera de la tarjeta. */}
                  <span className="block truncate text-[11.5px] text-white/50">
                    {t(earner.count === 1 ? "{{n}} comisión" : "{{n}} comisiones", {
                      n: earner.count,
                    })}
                    {earner.owedCents > 0 &&
                      ` · ${t("debe {{monto}}", { monto: formatPrice(earner.owedCents, "MXN") })}`}
                  </span>
                </span>
                {/*
                  `shrink-0`: el monto es EL dato de la fila y no puede
                  ceder ancho ni salirse por el borde. Quien cede es el
                  nombre, que ya trunca. Un punto menos de tamaño y algo
                  menos de `tracking` para que quepa una cifra de cinco
                  dígitos sin empujar.
                */}
                <span className="shrink-0 font-mono text-[13px] font-semibold tracking-tight">
                  {formatPrice(earner.totalCents, "MXN")}
                </span>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
